import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Popover,
  Box,
  Typography,
  Radio,
  TextField,
  Button,
  Divider,
  Collapse,
  makeStyles,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Inbox as UngroupedIcon,
  ChevronRight as ChevronRightIcon,
} from "@material-ui/icons";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  popover: {
    padding: theme.spacing(2),
    minWidth: 300,
    maxWidth: 400,
  },
  title: {
    fontWeight: 500,
    marginBottom: theme.spacing(2),
  },
  folderList: {
    maxHeight: 250,
    overflowY: "auto",
    marginBottom: theme.spacing(1),
  },
  folderRow: {
    display: "flex",
    alignItems: "center",
    padding: "4px 0",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  folderRowSelected: {
    backgroundColor: (props) => `${props.clientColor}15`,
  },
  expandIcon: {
    fontSize: "1.2rem",
    color: theme.palette.text.secondary,
    cursor: "pointer",
    marginRight: 4,
    transition: "transform 0.2s",
  },
  expandIconOpen: {
    transform: "rotate(90deg)",
  },
  expandIconPlaceholder: {
    width: "1.2rem",
    marginRight: 4,
  },
  folderIcon: {
    fontSize: "1.1rem",
    marginRight: 8,
  },
  ungroupedIcon: {
    fontSize: "1.1rem",
    color: theme.palette.text.secondary,
    marginRight: 8,
  },
  folderName: {
    flex: 1,
    fontSize: "0.875rem",
  },
  radio: {
    padding: 4,
    marginRight: 4,
    color: (props) => `${props.clientColor} !important`,
    "&.Mui-checked": {
      color: (props) => `${props.clientColor} !important`,
    },
  },
  createSection: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  createInput: {
    marginTop: theme.spacing(1),
    "& .MuiInput-underline": {
      "&:after": {
        borderBottomColor: (props) => `${props.clientColor} !important`,
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: (props) => `${props.clientColor} !important`,
    },
  },
  createActions: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));

const FolderSelectPopover = ({
  open,
  anchorEl,
  folders,
  folderIdMap = {},
  onCancel,
  onConfirm,
  onCreateFolder,
  isSubmitting = false,
  clientColor,
}) => {
  const classes = useStyles({ clientColor });
  const { t } = useTranslation("translation", {
    keyPrefix: "rgm.cockpit.scenario_manager",
  });
  const [selectedFolderId, setSelectedFolderId] = useState(null); // null = no folder
  const [expandedFolders, setExpandedFolders] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");

  // Auto-update selection when temp ID is resolved to real ID
  useEffect(() => {
    if (selectedFolderId && folderIdMap[selectedFolderId]) {
      setSelectedFolderId(folderIdMap[selectedFolderId]);
    }
  }, [folderIdMap, selectedFolderId]);

  const handleConfirm = () => {
    // Pass selected folder ID - parent will resolve temp IDs
    onConfirm(selectedFolderId);
  };

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return;

    // Call parent handler which returns temp ID immediately (optimistic)
    const newFolderId = onCreateFolder(newFolderName.trim());
    // Select the new folder and close create form
    setSelectedFolderId(newFolderId);
    setNewFolderName("");
    setIsCreating(false);
  };

  const toggleFolder = (folderId) => {
    setExpandedFolders((prev) =>
      prev.includes(folderId)
        ? prev.filter((id) => id !== folderId)
        : [...prev, folderId],
    );
  };

  const isFolderExpanded = (folderId) => expandedFolders.includes(folderId);

  // Render folder tree recursively
  const renderFolder = (folder, depth = 0) => {
    const hasChildren = folder.subfolders?.length > 0;
    const isExpanded = isFolderExpanded(folder.folder_id);
    const isSelected = selectedFolderId === folder.folder_id;

    return (
      <React.Fragment key={folder.folder_id}>
        <Box
          className={`${classes.folderRow} ${isSelected ? classes.folderRowSelected : ""}`}
          style={{ paddingLeft: depth * 20 }}
          onClick={() => setSelectedFolderId(folder.folder_id)}
        >
          <Radio
            size="small"
            className={classes.radio}
            checked={isSelected}
            onChange={() => setSelectedFolderId(folder.folder_id)}
          />
          {hasChildren ? (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                toggleFolder(folder.folder_id);
              }}
              style={{ display: "flex", alignItems: "center" }}
            >
              <ChevronRightIcon
                className={`${classes.expandIcon} ${isExpanded ? classes.expandIconOpen : ""}`}
              />
            </Box>
          ) : (
            <Box className={classes.expandIconPlaceholder} />
          )}
          {isExpanded ? (
            <FolderOpenIcon
              className={classes.folderIcon}
              style={{ color: folder.color || "#6B7280" }}
            />
          ) : (
            <FolderIcon
              className={classes.folderIcon}
              style={{ color: folder.color || "#6B7280" }}
            />
          )}
          <Typography className={classes.folderName}>{folder.name}</Typography>
        </Box>
        {hasChildren && (
          <Collapse in={isExpanded}>
            {folder.subfolders.map((subfolder) =>
              renderFolder(subfolder, depth + 1),
            )}
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onCancel}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      transformOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Box className={classes.popover}>
        <Typography className={classes.title}>
          {t("folder_select.title")}
        </Typography>

        <Box className={classes.folderList}>
          {/* Ungrouped option */}
          <Box
            className={`${classes.folderRow} ${selectedFolderId === null ? classes.folderRowSelected : ""}`}
            onClick={() => setSelectedFolderId(null)}
          >
            <Radio
              size="small"
              className={classes.radio}
              checked={selectedFolderId === null}
              onChange={() => setSelectedFolderId(null)}
            />
            <Box className={classes.expandIconPlaceholder} />
            <UngroupedIcon className={classes.ungroupedIcon} />
            <Typography className={classes.folderName}>
              {t("sidebar.ungrouped")}
            </Typography>
          </Box>

          {/* Folder tree */}
          {(folders || []).map((folder) => renderFolder(folder, 0))}
        </Box>

        <Divider />

        <Box className={classes.createSection}>
          {isCreating ? (
            <Box>
              <TextField
                size="small"
                fullWidth
                placeholder={t("folder_create.placeholder")}
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                className={classes.createInput}
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === "Enter") handleCreateFolder();
                }}
              />
              <Box className={classes.createActions}>
                <Button
                  size="small"
                  onClick={handleCreateFolder}
                  disabled={!newFolderName.trim()}
                >
                  {t("folder_create.create")}
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setIsCreating(false);
                    setNewFolderName("");
                  }}
                >
                  {t("folder_create.cancel")}
                </Button>
              </Box>
            </Box>
          ) : (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setIsCreating(true)}
            >
              {t("breadcrumb.new_folder")}
            </Button>
          )}
        </Box>

        <Box className={classes.actions}>
          <Button onClick={onCancel} disabled={isSubmitting}>
            {t("folder_create.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={isSubmitting}
            style={{ backgroundColor: clientColor, color: "white" }}
          >
            {isSubmitting
              ? t("folder_select.running")
              : t("folder_select.run_calculations")}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

FolderSelectPopover.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.object,
  folders: PropTypes.array.isRequired,
  folderIdMap: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCreateFolder: PropTypes.func,
  isSubmitting: PropTypes.bool,
  clientColor: PropTypes.string,
};

export default FolderSelectPopover;
